import React from 'react';
import ReactDOM from 'react-dom';
import './NavigationSearch.css';

// Modul-Titel-Dropdown (Foren, favorisierte/offene Module, Mitglieder) - wird
// als Trigger in der Modul-Kopfzeile eingebunden (Components/Core/Module.js).
// Die frueher zusaetzlich in der gruenen Top-Navigation permanent sichtbare
// bzw. per Signpost-Icon einblendbare "Foren- & Mitgliederliste" wurde am
// 2026-07-26 ersatzlos gestrichen (Nutzervorgabe: verwirrend fuer Neulinge,
// redundant zu diesem Dropdown und der bestehenden Mitgliederliste) - seitdem
// gibt es nur noch diese eine, kompakte Variante mit Filterfeld.
export default function NavigationSearch(props) {

	const x = props.x;

	const [query, setQuery] = React.useState('');
	const [showResults, setShowResults] = React.useState(false);
	const [resultsRect, setResultsRect] = React.useState(null);
	const [forums, setForums] = React.useState([]);
	// Reines Render-Signal: die Favoriten selbst stehen in x.was.members (kein
	// State), eine Aenderung dort loest von sich aus kein Rendern aus.
	const [, setFavoritVersion] = React.useState(0);
	const forumsLoaded = React.useRef(false);
	const triggerRef = React.useRef(null);

	function loadForums() {
		if (forumsLoaded.current) return;
		forumsLoaded.current = true;
		// x.was.foren wird bereits einmalig beim App-Start befuellt (Webapp.js) - ein
		// eigener axios-Request hier war redundant und sorgte dafuer, dass die
		// "Foren"-Gruppe bei jedem ersten Oeffnen (pro Modul-Instanz) erst mit
		// spuerbarer Verzoegerung nachtraeglich einsprang.
		// Das Hauptforum ("forum") ist kein Eintrag der foren-Tabelle, muss hier
		// also als fester Eintrag ergaenzt werden, sonst fehlt der Weg zurueck
		// zum Hauptforum, sobald man in ein anderes Modul gewechselt hat. Name
		// analog zur Sidebar (MemberListBasis.js/MemberList.inc), die dafuer
		// ebenfalls den Instanz-Titel statt "Hauptforum" verwendet.
		const hauptforum = { id: 'forum', name: x.was.application };
		const forenList = Object.values(x.was.foren || {});
		setForums([hauptforum, ...forenList.filter((forum) => parseInt(forum.disabled, 10) !== 1)]);
	}

	// Das Portal (siehe unten) rendert ausserhalb von #webapp, wo die
	// Theme-Variablen per Inline-Style gesetzt werden (siehe WebappLayout.js).
	// CSS var(...) kaeme dort ins Leere, daher werden die aktuell gueltigen
	// Werte hier ausgelesen und dem Portal explizit mitgegeben.
	function getThemeStyle() {
		const webapp = document.getElementById('webapp');
		const computed = window.getComputedStyle(webapp);
		return {
			'--content-bg-color': computed.getPropertyValue('--content-bg-color'),
			'--content-bg-color-disabled': computed.getPropertyValue('--content-bg-color-disabled'),
			'--content-border-color': computed.getPropertyValue('--content-border-color'),
			'--forum-color': computed.getPropertyValue('--forum-color'),
			// Fuer den Ungelesene-Badge (navigation-search-result-unread) - ohne
			// diese explizite Weitergabe ist die Variable ausserhalb von #webapp
			// (Portal-Ziel) undefiniert, der Badge existiert dann zwar im DOM,
			// aber unsichtbar (transparenter Hintergrund).
			'--main-bg-color': computed.getPropertyValue('--main-bg-color'),
		};
	}

	function openResults() {
		loadForums();
		setResultsRect(triggerRef.current.getBoundingClientRect());
		setShowResults(true);
	}

	function toggleResults() {
		if (showResults) {
			reset();
		}
		else {
			openResults();
		}
	}

	function matches(name) {
		return query.trim() === '' || name.toLowerCase().includes(query.trim().toLowerCase());
	}

	const filteredForums = forums
		.filter((forum) => String(forum.id) !== String(x.wam.current))
		.filter((forum) => matches(forum.name));
	// Reihenfolge wie in der Sidebar (MemberListBasis.js/MemberList.inc): erst
	// Online, dann Favoriten, dann alphabetisch. Hier muss explizit sortiert
	// werden - x.was.members ist ein Objekt mit numerischen Schluesseln, dessen
	// Iterationsreihenfolge die Mitglieds-IDs aufsteigend liefert und die
	// Sortierung des Backends damit verwirft.
	const filteredMembers = Object.keys(x.was.members)
		.map((memberId) => ({ memberId, ...x.was.members[memberId] }))
		.filter((member) => parseInt(member.memberId, 10) !== parseInt(x.was.memberId, 10))
		.filter((member) => parseInt(member.locked, 10) !== 1)
		.filter((member) => matches(member.name))
		.sort((a, b) =>
			(parseInt(b.online, 10) || 0) - (parseInt(a.online, 10) || 0)
			|| (parseInt(b.favorit, 10) || 0) - (parseInt(a.favorit, 10) || 0)
			|| a.name.localeCompare(b.name, 'de', { sensitivity: 'base' })
		);

	function toggleFavorit(event, memberId) {
		// preventDefault haelt den Fokus im Filterfeld - sonst wuerde dessen
		// onBlur das Dropdown schliessen, sobald man einen Stern antippt.
		event.preventDefault();
		event.stopPropagation();
		x.was.toggleMemberFavorit?.(memberId, () => setFavoritVersion((version) => version + 1));
	}
	function getModuleTitle(moduleId) {
		// Der Titel steht zuverlaessig schon in der Tag-Definition (Content.js), unabhaengig
		// davon, ob das Modul in dieser Sitzung bereits gemountet wurde (x.wam[id].title
		// existiert erst nach dem ersten Rendern des Moduls und ist bis dahin ein
		// generischer Platzhalter).
		const tag = x.wam.tags && x.wam.tags[moduleId];
		if (tag && typeof tag.props.title !== 'undefined') return tag.props.title;
		if (typeof x.wam[moduleId] !== 'undefined' && typeof x.wam[moduleId].title !== 'undefined') return x.wam[moduleId].title;
		return null;
	}

	const filteredFavoriteModules = (x.was.favoriteModules || [])
		.filter((moduleId) => String(moduleId) !== String(x.wam.current))
		.map((moduleId) => ({ moduleId, title: getModuleTitle(moduleId) }))
		.filter((favoriteModule) => favoriteModule.title !== null)
		.filter((favoriteModule) => matches(favoriteModule.title));

	// Bereits geoeffnete Module (unabhaengig von Favoriten) - Foren werden hier
	// ausgeschlossen, da sie schon in der Foren-Gruppe stehen, ebenso das aktuell
	// aktive Modul und alles, was schon unter "Weitere favorisierte Module" steht.
	const filteredOpenModules = Object.entries(x.wam)
		.filter(([, value]) => typeof value === 'object' && value !== null && typeof value.moduleType !== 'undefined')
		.filter(([, value]) => value.moduleType !== 'system' && value.moduleType !== 'forum')
		.filter(([moduleId]) => moduleId !== x.wam.current)
		.filter(([moduleId]) => !(x.was.favoriteModules || []).includes(moduleId))
		.map(([moduleId, value]) => ({ moduleId, title: value.title }))
		.filter((openModule) => typeof openModule.title !== 'undefined')
		.filter((openModule) => matches(openModule.title));

	// Ungelesene-Zaehler analog zur Desktop-Sidebar (MemberListBasis.js) -
	// x.was.postingsUnread ist dort das zentrale, laufend aktualisierte Cache-
	// Objekt: fuer Foren direkt per Foren-ID (bzw. "forum" fuers Hauptforum)
	// indiziert, fuer private Unterhaltungen per "meineId-partnerId"-Schluessel
	// (Reihenfolge nicht garantiert, daher ueber getConversationalPartner
	// aufgeloest statt den Schluessel selbst zusammenzusetzen).
	function forumUnread(forumId) {
		return x.was.postingsUnread[forumId] || 0;
	}

	function memberUnread(memberId) {
		let count = 0;
		Object.entries(x.was.postingsUnread).forEach(([key, value]) => {
			if (key !== 'forum' && key.includes('-') && String(x.wau.getConversationalPartner(key)) === String(memberId)) {
				count += value;
			}
		});
		return count;
	}

	function unreadBadge(count) {
		if (!count) return null;
		return <span className="navigation-search-result-unread">{(count > 999) ? '++' : count}</span>;
	}

	function openForum(forum) {
		x.wam.openForum(String(forum.id), forum.name);
		reset();
	}

	function openMember(member) {
		x.wam.openPrivateForum(member.memberId, member.name, member.level, true);
		reset();
	}

	function openFavoriteModule(favoriteModule) {
		x.wam.activate(favoriteModule.moduleId);
		reset();
	}

	function openOpenModule(openModule) {
		x.wam.activate(openModule.moduleId);
		reset();
	}

	function reset() {
		setQuery('');
		setShowResults(false);
	}

	const results = (showResults && resultsRect) &&
		<div
			className="navigation-search-results navigation-search-results-compact"
			style={{
				top: resultsRect.bottom + 4,
				left: resultsRect.left,
				width: 'auto',
				// Statt einer festen max-height (fruehher 400px), die bei wenigen
				// Eintraegen ungenutzten Platz darunter frei liess: die Box selbst
				// bis knapp an den unteren Bildschirmrand ausdehnen (nicht nur als
				// Scroll-Obergrenze), damit sie immer bis dorthin reicht.
				height: window.innerHeight - resultsRect.bottom - 20,
				...getThemeStyle(),
			}}
		>
			<input
				type="text"
				className="navigation-search-compact-filter"
				placeholder="Filtern…"
				value={query}
				onChange={(e) => setQuery(e.target.value)}
				onBlur={() => setTimeout(reset, 200)}
				onKeyDown={(e) => { if (e.key === 'Escape') { e.target.blur(); reset(); } }}
				autoFocus
			/>
			{filteredForums.length > 0 &&
				<div className="navigation-search-group-label">Foren</div>
			}
			{filteredForums.map((forum) =>
				<div
					key={'forum-' + forum.id}
					className="navigation-search-result navigation-search-result-forum"
					onMouseDown={() => openForum(forum)}
				>
					<i className="bi bi-chat-square-text navigation-search-result-icon" />
					<span className="navigation-search-result-label">{forum.name}</span>
					{unreadBadge(forumUnread(forum.id))}
				</div>
			)}
			{filteredFavoriteModules.length > 0 &&
				<div className="navigation-search-group-label">Weitere favorisierte Module</div>
			}
			{filteredFavoriteModules.map((favoriteModule) =>
				<div
					key={'favorite-' + favoriteModule.moduleId}
					className="navigation-search-result navigation-search-result-favorite"
					onMouseDown={() => openFavoriteModule(favoriteModule)}
				>
					<i className="bi bi-star-fill navigation-search-result-icon" />
					{favoriteModule.title}
				</div>
			)}
			{filteredOpenModules.length > 0 &&
				<div className="navigation-search-group-label">Geöffnete Module</div>
			}
			{filteredOpenModules.map((openModule) =>
				<div
					key={'open-' + openModule.moduleId}
					className="navigation-search-result navigation-search-result-open"
					onMouseDown={() => openOpenModule(openModule)}
				>
					<i className="bi bi-window navigation-search-result-icon" />
					{openModule.title}
				</div>
			)}
			{filteredMembers.length > 0 &&
				<div className="navigation-search-group-label">Mitglieder</div>
			}
			{filteredMembers.map((member) =>
				<div
					key={'member-' + member.memberId}
					className="navigation-search-result navigation-search-result-member"
					onMouseDown={() => openMember(member)}
				>
					<i className="bi bi-person navigation-search-result-icon" />
					<i className={`bi ${(parseInt(member.online, 10) === 1) ? 'bi-circle-fill' : 'bi-circle'} navigation-search-result-online`} title="Online-Status" />
					<span className="navigation-search-result-label">{member.name}</span>
					{unreadBadge(memberUnread(member.memberId))}
					<i
						className={`bi ${(parseInt(member.favorit, 10) === 1) ? 'bi-star-fill' : 'bi-star'} navigation-search-result-favorit ${(parseInt(member.favorit, 10) === 1) ? 'navigation-search-result-favorit-active' : ''}`}
						title={(parseInt(member.favorit, 10) === 1) ? 'Favorit entfernen' : 'Als Favorit markieren'}
						onMouseDown={(e) => toggleFavorit(e, member.memberId)}
					/>
				</div>
			)}
			{filteredForums.length === 0 && filteredMembers.length === 0 && filteredFavoriteModules.length === 0 && filteredOpenModules.length === 0 &&
				<div className="navigation-search-result navigation-search-no-result">Keine Treffer</div>
			}
		</div>

	return (
		<div id="navigation-search" className="navigation-search navigation-search-compact">
			<span
				ref={triggerRef}
				role="button"
				tabIndex={0}
				className="navigation-search-compact-trigger"
				onClick={toggleResults}
				onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleResults(); } }}
				// Kein onBlur hier (Chat vom 2026-07-26): sobald sich das Filterfeld
				// im Dropdown selbst per autoFocus fokussiert, wuerde dieser Span
				// sonst sofort unfreiwillig einen verzoegerten reset() ausloesen und
				// das Dropdown mitten beim Tippen schliessen. Das Schliessen bei
				// Fokusverlust uebernimmt das Filterfeld selbst.
			>
				<span className="navigation-search-compact-label">{props.triggerLabel}</span>
				<i className="bi bi-chevron-down navigation-search-compact-icon" />
			</span>
			{results && ReactDOM.createPortal(results, document.body)}
		</div>
	);

}
